<template>
  <b-card class="project-files-card shadow">
    <modal
      click-to-close
      name="delete-file-dialog"
      classes="modalBoon"
      transition="nice-modal-fade"
      :delay="100"
      :width="450"
    >
      <h2>{{ $t('Delete a file?') }}</h2>
      <p>{{ $t('Are you sure you want to delete the file of {fileName}?', { fileName: $t(fileToDelete) || '??' }) }}</p>
      <p>{{ $t('This action cannot be undone!') }}</p>
      <ul class="modalButtons">
        <li>
          <button class="darkButton" @click="onConfirmedDeleteFileClick">{{ $t('Delete') }}</button>
        </li>
        <li>
          <button class="lightButton" @click="onCancelDeleteFileClick">{{ $t('Cancel') }}</button>
        </li>
      </ul>
    </modal>

    <upload-project-file-dialog
      v-if="uploadFile.show"
      :visible="uploadFile.show"
      @close="uploadFile.show = false"
      @cancel="uploadFile.show = false"
      @confirm="onUploadFileConfirm"
    />

    <b-table hover striped :items="items" :fields="fields" class="project-files-table mb-0">
      <template #table-colgroup>
        <col :style="{ width: '29%' }" />
        <col :style="{ width: '24%' }" />
        <col :style="{ width: '31%' }" />
        <col :style="{ width: '16%' }" />
      </template>

      <template #cell(fileName)="{ item }">
        <s3-file-download-button v-if="item.fileName !== 'codeOfConduct'" :s3Key="item.s3Key" class="mr-4" />
        <span v-else style="width: 50px" class="d-inline-block mr-4">&nbsp;</span>
        <span class="font-weight-bold">{{ $t(item.fileName.split('.')[0]) }}</span>
      </template>

      <template #cell(status)="{ item }">
        <code-of-conduct-status-control
          v-if="item.fileName === 'codeOfConduct'"
          :value="item.status"
          :disabled="documentRemoving[item.fileName]"
          :isBusy="statusChanging[item.fileName]"
          @input="changeCodeOfConductStatus"
        />
        <status-control
          v-else
          :value="item.status"
          :disabled="documentRemoving[item.fileName]"
          :isBusy="statusChanging[item.fileName]"
          @input="($event) => changeStatus($event, item.fileName)"
          @click:not-uploaded="() => showUploadFileDialog(item.fileName)"
        />
      </template>

      <template #cell(approve)="{ item }">
        {{ [item.approvingPerson, item.approvingDate].filter(Boolean).join(', ') }}
      </template>

      <template #cell(actions)="{ item }">
        <div v-if="item.s3Key && item.fileName !== 'codeOfConduct'" class="actions-block float-right">
          <div v-if="!statusChanging[item.fileName]" class="float-right mr-3">
            <b-spinner v-if="documentRemoving[item.fileName]" small class="align-middle" />
            <b-icon v-else icon="trash-fill" @click="() => removeCompanyFile(item.fileName)" />
          </div>
        </div>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import moment from 'moment';

import constants from '@/constants';

import { EventBus } from '@/common/eventbus';

import StatusControl from '@/components/organisation/EditCompany/StatusControl';
import CodeOfConductStatusControl from '@/components/organisation/EditCompany/CodeOfConductStatusControl';
import S3FileDownloadButton from '@/components/common/S3FileDownloadButton';
import UploadProjectFileDialog from './UploadProjectFileDialog';

import {
  changeProjectFileStatus,
  changeCodeOfConductStatus,
  addProjectFile,
  deleteProjectFile,
} from '@/store/companyFiles/actionTypes';
import { byCompanyId as getCompanyFilesByCompanyId } from '@/store/companyFiles/getterTypes';
import { mapCompanyFilesActions, mapCompanyFilesGetters } from '@/store/companyFiles/namespace';

export default {
  name: 'ProjectFiles',

  components: {
    StatusControl,
    CodeOfConductStatusControl,
    S3FileDownloadButton,
    UploadProjectFileDialog,
  },

  props: {
    companyId: {
      type: String,
      requied: true,
    },
    projectId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fields: [
        { key: 'fileName', class: 'cell-file-name' },
        { key: 'approve', class: 'cell-approve' },
        { key: 'actions', class: 'cell-actions' },
        { key: 'status', class: 'cell-status' },
      ],
      fileToDelete: null,
      documentRemoving: {},
      statusChanging: {},
      uploadFile: {
        show: false,
        fileName: null,
      },
    };
  },

  computed: {
    ...mapCompanyFilesGetters([getCompanyFilesByCompanyId]),

    filesDoc() {
      return this[getCompanyFilesByCompanyId](this.companyId);
    },

    files() {
      return (
        (this.filesDoc &&
          this.filesDoc.projectFiles &&
          this.filesDoc.projectFiles[this.projectId] &&
          this.filesDoc.projectFiles[this.projectId].files) ||
        []
      );
    },

    codeOfConduct() {
      return (
        (this.filesDoc &&
          this.filesDoc.projectFiles &&
          this.filesDoc.projectFiles[this.projectId] &&
          this.filesDoc.projectFiles[this.projectId].codeOfConduct) ||
        {}
      );
    },

    items() {
      const files = [...this.files];
      constants.companyFiles.names.forEach((fileName) => {
        if (!files.some((item) => item.fileName === fileName)) {
          files.push({ fileName, status: constants.companyFiles.statuses.NOT_LOADED });
        }
      });

      const result = files.map((file) => ({
        ...file,
        approvingDate:
          file.approvingDate == null ? null : moment(file.approvingDate).format(constants.formats.dateShort),
      }));

      result.push({
        fileName: 'codeOfConduct',
        approvingDate:
          this.codeOfConduct.approvedAt == null
            ? null
            : moment(this.codeOfConduct.approvingDate).format(constants.formats.dateShort),
        status: this.codeOfConduct.status,
      });

      return result;
    },
  },

  methods: {
    ...mapCompanyFilesActions([changeProjectFileStatus, changeCodeOfConductStatus, addProjectFile, deleteProjectFile]),

    hasFiles(files) {
      return !!files && files.some(({ status }) => !!status && status !== constants.companyFiles.statuses.NOT_LOADED);
    },

    async changeStatus(status, fileName) {
      this.statusChanging = {
        ...this.statusChanging,
        [fileName]: true,
      };

      try {
        await this[changeProjectFileStatus]({
          companyId: this.companyId,
          projectId: this.projectId,
          fileName,
          status,
        });
      } catch (error) {
        console.error(error);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to update file status'),
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.statusChanging = {
          ...this.statusChanging,
          [fileName]: false,
        };
      }
    },

    async changeCodeOfConductStatus(status) {
      this.statusChanging = {
        ...this.statusChanging,
        codeOfConduct: true,
      };

      try {
        await this[changeCodeOfConductStatus]({
          companyId: this.companyId,
          projectId: this.projectId,
          status,
        });
      } catch (error) {
        console.error(error);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to update code of conduct status'),
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.statusChanging = {
          ...this.statusChanging,
          codeOfConduct: false,
        };
      }
    },

    removeCompanyFile(fileName) {
      this.fileToDelete = fileName;
      this.openDeleteFileDialog();
    },

    showUploadFileDialog(fileName) {
      this.uploadFile = { fileName, show: true };
    },

    async onUploadFileConfirm(fileInfo, fileArrayBuffer) {
      this.statusChanging = {
        ...this.statusChanging,
        [this.uploadFile.fileName]: true,
      };

      try {
        await this[addProjectFile]({
          companyId: this.companyId,
          projectId: this.projectId,
          fileName: this.uploadFile.fileName,
          fileArrayBuffer,
          fileInfo,
        });
      } catch (error) {
        console.error(error);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to upload file'),
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.statusChanging = {
          ...this.statusChanging,
          [this.uploadFile.fileName]: false,
        };
        this.uploadFile = { fileName: null, show: false };
      }
    },

    openDeleteFileDialog() {
      this.$modal.show('delete-file-dialog', { title: 'Delete a file?' });
    },

    closeDeleteFileDialog() {
      this.$modal.hide('delete-file-dialog');
    },

    async onConfirmedDeleteFileClick() {
      if (!this.fileToDelete) {
        return;
      }

      this.closeDeleteFileDialog();

      this.documentRemoving = {
        ...this.documentRemoving,
        [this.fileToDelete]: true,
      };

      try {
        await this[deleteProjectFile]({
          companyId: this.companyId,
          projectId: this.projectId,
          fileName: this.fileToDelete,
        });
      } catch (error) {
        console.error(error);
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to delete file'),
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.documentRemoving = {
          ...this.documentRemoving,
          [this.fileToDelete]: false,
        };

        this.fileToDelete = null;
      }
    },

    onCancelDeleteFileClick() {
      this.closeDeleteFileDialog();
    },
  },
};
</script>

<style scoped>
.actions-block {
  visibility: hidden;
  display: inline-block;
}

.actions-block .b-icon {
  cursor: pointer;
}

.project-files-table tr:hover .actions-block {
  visibility: visible;
}

.project-files-card >>> .card-body {
  padding: 0;
}

.project-files-card >>> .card-body > h4 {
  padding: 0 15px;
}

.project-files-table >>> tbody {
  border-top: 1px !important;
}

.project-files-table >>> thead {
  display: none;
}

.project-files-table >>> tbody > tr > td {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/EditCompany/CompanyFiles/ProjectFiles/index.vue